import { callFunction } from "../api/callFunction";
import { showToast } from "../utils/showToast";
import EventAPI from "../api/EventApi";
import EventSuggestionAPI from "../api/EventSuggestionApi";
import EventRequestAPI from "../api/EventRequestApi";
import UserAPI from "../api/UserApi";
import {
  uploadEventPhotos,
  uploadListingVariantPhotos,
  hasPendingVariantPhoto,
  buildEventUsers,
  validateEventFields,
} from "./utils/formUtils";
import { capture, EVENTS } from "../analytics";
import { eventAccessFor, withoutPrimaryOnlyFields } from "./utils/eventHosts";
import { createFailedProps } from "./utils/createFailedProps";

/**
 * The event-scoped property set (taxonomy §3), built from the in-progress form
 * object rather than a saved doc.
 *
 * `eventID` is deliberately absent on the create path: the doc ref does not
 * exist until the write, and the whole point of `admin_event_create_submitted`
 * is that it fires for creates that never produce one. Callers that have an id
 * pass it in.
 *
 * `region` is omitted rather than sent as null. The panel's field allowlist has
 * no lat/lng — geocoding happens server-side after the write — so this surface
 * genuinely cannot resolve one, and a null column that is null 100% of the time
 * reads as a broken tag rather than as "not applicable here".
 */
function eventFormProps(event) {
  const cost = Number(event?.cost) || 0;
  const tiers = Array.isArray(event?.priceSchedule) ? event.priceSchedule : [];
  return {
    // NOT `cost > 0`. The admin form seeds cost:0 alongside a $10/$15/$20/$25
    // ladder, so the common event reads free at creation and charges as the
    // date nears (taxonomy §4).
    isPaid: cost > 0 || tiers.some((tier) => Number(tier?.price) > 0),
    isPaidSource: tiers.length ? "cost_and_schedule" : "cost_only",
    listPriceCents: Math.round(cost * 100),
    hasPriceSchedule: tiers.length > 0,
    photoCount: Array.isArray(event?.photos) ? event.photos.length : 0,
    videoEnabled: event?.videoEnabled === true,
    requireCheckIn: event?.requireCheckIn !== false,
  };
}

/**
 * A cover image for an event the host didn't give one, so it has something for
 * the video pipeline (and the feed card) to work from. Returns a data URL, or
 * null if generation failed — the caller treats that as "save without a photo".
 */
async function generateCoverImage(event) {
  try {
    const result = await callFunction("generateImageForEvent", {
      name: event.name,
      date: event.date,
      description: event.description,
      hostname: event.hostname,
      address: event.address,
    });
    const imageData = result?.imageData;
    return imageData ? `data:image/png;base64,${imageData}` : null;
  } catch (error) {
    console.error("Error generating cover image:", error);
    return null;
  }
}

export const useEventActions = ({
  event,
  eventData,
  usersDb,
  globalState,
  sourceSuggestionId,
  sourceRequestId,
  photoGateBlocked,
  notifyDraft,
  navigate,
  setSubmitting,
  setProgressMessage,
  setDateNotify,
  setSendingNotify,
}) => {
  // Cancellation runs through the CancelEventWizard (refund → note → discount →
  // finalize), which calls EventAPI.cancelEvent itself. No handler here.

  const handleDeleteEvent = async (e) => {
    e.preventDefault();
    if (!eventData?.id) return;
    // Two ways to land here: a cancelled event, or an event nobody signed up for
    // (which skips cancellation entirely). Name which one, so the host isn't
    // asked to confirm deleting a "cancelled" event they never cancelled.
    const message =
      eventData?.status === "cancelled"
        ? "Permanently delete this cancelled event? This cannot be undone."
        : "Permanently delete this event? Nobody has signed up, so no one will be notified. This cannot be undone.";
    if (!window.confirm(message)) return;
    setSubmitting(true);
    setProgressMessage("Deleting event…");
    try {
      await callFunction("deleteEvent", { eventID: eventData.id }, { timeout: 60000 });
      showToast("Event deleted successfully!", { type: "success" });
      navigate("/events");
    } catch (error) {
      console.error("deleteEvent failed:", error);
      // Backend refuses to delete an event with signups unless it's already
      // cancelled — its message names the next step, so surface it verbatim.
      showToast(error?.message || "Failed to delete event.", { type: "error" });
    } finally {
      setSubmitting(false);
      setProgressMessage("");
    }
  };

  const handleUncancelEvent = async (e) => {
    e.preventDefault();
    if (!eventData?.id) return;
    if (
      !window.confirm(
        "Restore this event? It will return to upcoming events and become visible again."
      )
    )
      return;
    setSubmitting(true);
    setProgressMessage("Restoring event…");
    try {
      await callFunction("uncancelEvent", { eventID: eventData.id }, { timeout: 60000 });
      // eventData is a live Firestore stream, so the cleared status flows in
      // automatically and swaps the buttons back to "Cancel event".
      showToast("Event restored.", { type: "success" });
    } catch (error) {
      console.error("uncancelEvent failed:", error);
      showToast(error?.message || "Failed to restore event.", { type: "error" });
    } finally {
      setSubmitting(false);
      setProgressMessage("");
    }
  };

  // Returns true when the event was actually saved, false on validation
  // abort/error — so the date-change notify flow only messages attendees once
  // the new date is committed.
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setProgressMessage("Validating…");

    const isCreate = !event.id;

    // Only the create path is instrumented. An edit that fails is a bug report,
    // not a funnel step, and `admin_event_create_failed` firing on a save of a
    // long-live event would make the create funnel's drop-off unreadable.
    // Sent as `failureReason`: the sanitizer drops a key called `reason` (GIS-273).
    const captureCreateFailed = (create, failureReason, extra) => {
      if (!create) return;
      capture(EVENTS.EVENT_CREATE_FAILED, createFailedProps(eventFormProps(event), failureReason, extra));
    };

    // Fired here — at the top, before the photo gate and before validation —
    // rather than next to the write, and that placement is the whole design.
    //
    // The funnel this feeds is "a host meant to create an event; did one come
    // out the other end?" Every early return below is a way to press Create and
    // get nothing: the photo gate, a validation error, declining the warnings
    // confirm. Firing after any of those would silently drop the exact
    // population the funnel exists to count, and the drop would be invisible —
    // the number would just look healthy.
    //
    // Pairs with the server's `event_created` rather than duplicating it: this
    // is intent, that is outcome, and the gap between them is the answer.
    if (isCreate) {
      capture(EVENTS.EVENT_CREATE_SUBMITTED, {
        ...eventFormProps(event),
        createdByRole: globalState?.isSuperAdmin ? "superAdmin" : "host",
        fromSuggestion: !!sourceSuggestionId,
        fromRequest: !!sourceRequestId,
      });
    }

    // Host profile-photo gate: a host must have a profile photo that passed
    // review before creating an event. Re-check the live user doc here so a
    // stale cache can't slip an unvalidated host past the gate (the disabled
    // button can also be bypassed by pressing Enter in a field). Super admins
    // and edits to an existing event are exempt.
    if (!event.id && !globalState?.isSuperAdmin) {
      let validated = !photoGateBlocked;
      const uid = globalState?.user?.userID || globalState?.vendorID;
      if (uid) {
        try {
          const user = await UserAPI.getUserById(uid);
          validated = user?.photoValidation?.has_single_face === true;
        } catch (err) {
          console.error("Failed to verify host photo validation:", err);
        }
      }
      if (!validated) {
        showToast(
          "Add a profile photo that passes review before you can create an event. You can update it in Settings.",
          { type: "error" }
        );
        captureCreateFailed(isCreate, "photo_gate");
        setSubmitting(false);
        setProgressMessage("");
        return false;
      }
    }

    // Capacity is checked on save only (GIS-266): the image generators run the
    // same validation before a host has got as far as choosing one.
    const { errors, warnings } = validateEventFields(
      event,
      true,
      !!eventData?.id,
      { checkCapacity: true }
    );
    if (errors.length > 0) {
      showToast(errors.join(", "), { type: "error" });
      // The messages themselves are never sent. They interpolate field values
      // (venue, host name, address), and the sanitizer's key blocklist cannot
      // help with a joined string under a key called `errors`.
      captureCreateFailed(isCreate, "validation", { errorCount: errors.length });
      setSubmitting(false);
      setProgressMessage("");
      return false;
    }
    if (warnings.length > 0) {
      const proceed = window.confirm(
        `${warnings.join("\n\n")}\n\nSave anyway?`
      );
      if (!proceed) {
        captureCreateFailed(isCreate, "warnings_declined", {
          warningCount: warnings.length,
        });
        setSubmitting(false);
        setProgressMessage("");
        return false;
      }
    }

    const eventDate =
      event.date instanceof Date ? event.date : new Date();

    // An event with no photo has nothing to animate, and the video pipeline
    // starts from the photo rather than from the event. So a cover image is
    // generated here, before the create, and then rides the normal upload path.
    // Only when the host actually wants a video: the image exists to feed it, so
    // an unticked box means there is nothing to feed and no reason to spend.
    // Best-effort by design: a failure here must not cost the host their event,
    // it just means no video (adding a photo later starts one).
    let photos = event.photos || [];
    if (!event.id && photos.length === 0 && event.videoEnabled === true) {
      setProgressMessage("Creating a cover image…");
      const generated = await generateCoverImage(event);
      if (generated) {
        photos = [generated];
      } else {
        showToast("Could not create a cover image. Saving without one.", { type: "warning" });
      }
    }

    const photoCount = photos.length;

    try {
      if (event.id) {
        // Update existing event
        setProgressMessage(
          photoCount > 0 ? `Uploading ${photoCount} photo${photoCount === 1 ? "" : "s"}…` : "Preparing update…"
        );
        const photoURLs = await uploadEventPhotos(photos, event.id);
        // A second listing's photo arrives here the same way an event photo
        // does, as a File or a base64 data URL, and `listingVariants` is written
        // straight into the doc. A base64 PNG usually blows Firestore's 1MB
        // document limit and fails the save, and a smaller one that landed would
        // be handed to Meetup and Eventbrite as an image URL. So it goes through
        // the same upload before the write.
        if (hasPendingVariantPhoto(event.listingVariants)) {
          setProgressMessage("Uploading the second listing's photo…");
        }
        const listingVariants = await uploadListingVariantPhotos(
          event.listingVariants,
          event.id
        );
        const eventUsers = buildEventUsers(event, usersDb);

        const updatedEventData = {
          ...event,
          photos: photoURLs,
          listingVariants,
          date: eventDate,
          group: event.group,
          wifi: event.wifi,
          cost: Number(event.cost) || 0,
          priceSchedule: event.priceSchedule || [],
          eventUsers,
        };

        if (!updatedEventData.wifi?.name && !updatedEventData.wifi?.password) {
          delete updatedEventData.wifi;
        }

        setProgressMessage("Saving changes…");
        // A co-host's save leaves price, active/completed and the host fields
        // alone (GIS-222): the rules refuse a co-host write that changes them,
        // and the form re-sends every field, defaults included.
        const { coHostOnly } = eventAccessFor(eventData, {
          uid: globalState?.vendorID,
          isSuperAdmin: globalState?.isSuperAdmin,
        });
        await EventAPI.patchEvent(event.id, coHostOnly ? withoutPrimaryOnlyFields(updatedEventData) : updatedEventData);
        showToast("Event updated successfully with photos!", { type: "success" });
        return true;
      } else {
        // Create new event. Pre-generate the doc id so the event's own `id`
        // field is written atomically in the initial create — no transient
        // no-id window and no dependency on a second write landing. Clients read
        // `{ ...doc.data(), id: doc.id }` and iOS decodes `id` as a required
        // non-null String, so the id must exist the moment the doc does.
        const eventID = EventAPI.newEventId();
        // Same substitution as the edit path. This one runs BEFORE the create
        // rather than in the photo pass that follows it, so the doc is never
        // written with a pending File or data URL inside `listingVariants`,
        // which is the write that would fail on the document size limit.
        if (hasPendingVariantPhoto(event.listingVariants)) {
          setProgressMessage("Uploading the second listing's photo…");
        }
        const listingVariants = await uploadListingVariantPhotos(
          event.listingVariants,
          eventID
        );
        const eventDataForSaving = {
          ...event,
          id: eventID,
          photos: [],
          listingVariants,
          date: eventDate,
          group: event.group,
          vendorID: globalState.vendorID,
          wifi: event.wifi,
          cost: Number(event.cost) || 0,
          priceSchedule: event.priceSchedule || [],
          // createdAt is stamped server-side by EventAPI.createEvent.
          // Creation provenance for analytics (GIS-206). onEventCreate fires from
          // onDocumentCreated, which carries no request.auth, so the server cannot
          // otherwise tell a host creating their own event from an operator
          // creating one on their behalf — every `event_created` shipped both as
          // null until this landed. Deliberately ONE FIELD written into the same
          // create rather than a second analytics event.
          //
          // `isSuperAdmin` is the same signal the photo gate above trusts (claim
          // OR the server-provisioned vendors flag), so the two cannot disagree
          // about who is an operator.
          createdByRole: globalState?.isSuperAdmin ? "superAdmin" : "host",
          createdVia: "admin_panel",
          // Provenance links when this event was converted from an AI suggestion
          // and/or a member event request, so we can trace/confirm which
          // suggestions & requests actually became events.
          ...(sourceSuggestionId ? { sourceSuggestionId } : {}),
          ...(sourceRequestId ? { sourceRequestId } : {}),
        };

        if (!eventDataForSaving.wifi?.name && !eventDataForSaving.wifi?.password) {
          delete eventDataForSaving.wifi;
        }

        setProgressMessage("Creating event…");
        await EventAPI.createEvent(eventID, eventDataForSaving);
        setProgressMessage(
          photoCount > 0 ? `Uploading ${photoCount} photo${photoCount === 1 ? "" : "s"}…` : "Finalizing…"
        );
        const photoURLs = await uploadEventPhotos(photos, eventID);
        if (photoURLs.length > 0) {
          setProgressMessage("Finalizing…");
          await EventAPI.patchEvent(eventID, { photos: photoURLs });
        }

        // The event now actually exists — only here do we mark the source
        // suggestion "converted" (rules let a holder write status + convertedAt on
        // a suggestion). Non-blocking: the event is already saved, so a failed
        // link-back must not surface as a create failure.
        if (sourceSuggestionId) {
          try {
            await EventSuggestionAPI.markConverted(sourceSuggestionId);
            // Fired HERE and not at the "Create event from this idea" click in
            // SuggestionCard, for the reason that comment spells out: opening
            // the form is not proof an event was created. This is inside the
            // try, after the durable write, so the event means what its name
            // says. A conversion the host abandoned mid-form is already counted
            // as `admin_event_draft_started` with origin "suggestion" and no
            // matching submit — which is the abandonment signal, not this.
            capture(EVENTS.SUGGESTION_CONVERTED, {
              eventID,
              suggestionID: sourceSuggestionId,
            });
          } catch (linkErr) {
            console.error("Failed to mark suggestion converted:", linkErr);
          }
        }

        // Same for a member event request: only now that the event exists do we
        // mark the request done (it was optimistically flipped to "approved" on
        // handoff before). eventsRequests has no field-level rules, so we can also
        // link the created event back. Non-blocking, same as above.
        if (sourceRequestId) {
          try {
            await EventRequestAPI.markConverted(sourceRequestId, eventID);
          } catch (reqErr) {
            console.error("Failed to mark request converted:", reqErr);
          }
        }

        showToast("Event created successfully with photos", { type: "success" });
        navigate(`/events/${eventID}/edit`);
        return true;
      }
    } catch (error) {
      console.error("Error saving event:", error);
      showToast("Failed to save event. Please try again.", { type: "error" });
      // Worth knowing this one can fire for a create that DID land: the doc
      // write succeeds, then photo upload or the follow-up update throws in
      // here. That is the same hazard that kept `event_created` server-side, and
      // it is why this event is named for the failure the operator SAW rather
      // than for the absence of a document. Reconcile against the server's
      // `event_created`, never treat it as authoritative on its own.
      captureCreateFailed(isCreate, "write_error", {
        errorCode: typeof error?.code === "string" ? error.code : "unknown",
      });
      return false;
    } finally {
      setSubmitting(false);
      setProgressMessage("");
    }
  };

  // Save the event (new date included), then post the pre-drafted heads-up to the
  // chat as a `notify` message — the same write the admin Chat panel uses, so
  // onChatMessage fans it out to SMS + push for everyone going. Saving first
  // guarantees we never announce a date that isn't committed. Author id mirrors
  // Chat.js: a host's vendorID (host broadcast guard) or admin userID (superAdmin).
  const handleSaveAndNotifyDateChange = async () => {
    if (!event.id || !notifyDraft.trim()) return;
    setSendingNotify(true);
    try {
      const saved = await handleSubmit();
      if (!saved) return; // save aborted/failed — handleSubmit already surfaced why
      const authorId = globalState?.vendor?.vendorID || globalState?.user?.userID;
      const authorName =
        globalState?.user?.name || globalState?.vendor?.name || "Admin";
      await EventAPI.sendMessage(
        event.id,
        "sittersChat",
        notifyDraft.trim(),
        authorId,
        authorName,
        "notify",
        "everyone"
      );
      showToast("Attendees notified about the new date.", { type: "success" });
      setDateNotify(null);
    } catch (error) {
      console.error("Failed to notify attendees of date change:", error);
      showToast(
        "Saved the date, but couldn't send the notification. You can post it from the event chat.",
        { type: "error" }
      );
    } finally {
      setSendingNotify(false);
    }
  };

  return {
    handleDeleteEvent,
    handleUncancelEvent,
    handleSubmit,
    handleSaveAndNotifyDateChange,
  };
};
